import React, { useEffect, useState } from "react";
import { SiteFooter } from "../components/SiteFooter";
import { LEGAL_ENABLED, SAFETY_ENABLED } from "../lib/flags";
import api from "../lib/api";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, MessageCircle, CalendarHeart, ShieldCheck, BadgeCheck, Sparkles, Flag, Lock, Wallet, MapPin, HelpCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { BrandLogo } from "../components/BrandLogo";
import { HONOLULU_GUIDES } from "../lib/honoluluGuides";

const STEPS = [
  { icon: Sparkles, t: "Discover", d: "Explore profiles and discover people who genuinely interest you.", img: "/steps/step-discover.webp" },
  { icon: ArrowRight, t: "Make an Offer", d: "Send a respectful date offer with a proposed amount — and, if you like, a date idea or message.", img: "/steps/step-offer.webp" },
  { icon: MessageCircle, t: "Connect", d: "The recipient can accept, decline, or counter. If they accept, a private conversation opens between you.", img: "/steps/step-connect.webp" },
  { icon: CalendarHeart, t: "Meet", d: "If you both agree, plan the date and choose a place to meet together.", img: "/steps/step-meet.webp" },
];

const TRUST = [
  { icon: ShieldCheck, t: "18+ only", d: "Every account must confirm a date of birth at sign-up. Anyone under 18 is blocked from joining." },
  { icon: BadgeCheck, t: "Identity verification", d: "Members can submit a selfie or photo ID for our team to review. A Verified badge appears only after approval — so always look for the badge rather than assuming everyone is verified." },
  { icon: Flag, t: "Report & block", d: "If someone makes you uncomfortable, block them instantly or report them to our team for review." },
  { icon: Lock, t: "Privacy by design", d: "Your date of birth is never shown publicly — only your age. You decide which photos are public and which stay private." },
];

const FAQS = [
  { q: "Is DateBid available in Honolulu?", a: "Yes — DateBid is built for singles across Oahu." },
  { q: "Do I have to pay to go on a date?", a: "No. Offers are voluntary, and any amount is arranged directly between members." },
  { q: "Is it safe?", a: "Members must be 18+, identity verification is available, and you can report or block anyone." },
];

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [banner, setBanner] = useState(null);
  useEffect(() => { if (user) navigate("/discover"); }, [user]);
  useEffect(() => { api.get("/public/launch-banner").then((r) => setBanner(r.data?.banner || null)).catch(() => {}); }, []);

  const seeHow = () => document.getElementById("how")?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className="db-grain min-h-screen relative" style={{ background: "#0C0A0A" }}>
      <nav className="relative z-20 flex items-center justify-between px-6 sm:px-10 py-5">
        <Link to="/" data-testid="landing-brand-logo" className="inline-flex items-center">
          <BrandLogo variant="wordmark" className="h-10 sm:h-12 w-auto" />
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/auth" data-testid="landing-signin-link" className="text-sm text-[#D8D2C9] hover:text-[#FBF8F3]">Sign in</Link>
          <Link to="/auth?mode=register" data-testid="landing-join-btn" className="db-offer-btn rounded-full px-5 py-2.5 text-sm font-semibold">Join DateBid</Link>
        </div>
      </nav>

      {banner && (
        <div data-testid="launch-banner" className="relative z-20 border-y border-[#D4AF37]/25 bg-gradient-to-r from-[#541224]/55 to-[#2a0f16]/40">
          <div className="max-w-7xl mx-auto px-6 sm:px-10 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <div className="flex-1 min-w-0">
              <span data-testid="launch-banner-headline" className="font-serif text-[#FBF8F3] text-sm sm:text-base">{banner.headline}</span>
              {banner.subtext && <span className="text-[#D8D2C9] text-xs sm:text-sm sm:ml-3 block sm:inline">{banner.subtext}</span>}
            </div>
            <div className="flex items-center gap-3 shrink-0 self-start sm:self-auto">
              {banner.cta_text && banner.cta_href && (
                banner.cta_href.startsWith("/")
                  ? <Link to={banner.cta_href} data-testid="launch-banner-cta" className="db-offer-btn rounded-full px-4 py-1.5 text-xs font-semibold whitespace-nowrap">{banner.cta_text}</Link>
                  : <a href={banner.cta_href} data-testid="launch-banner-cta" className="db-offer-btn rounded-full px-4 py-1.5 text-xs font-semibold whitespace-nowrap">{banner.cta_text}</a>
              )}
              <button type="button" aria-label="Dismiss banner" data-testid="launch-banner-dismiss" onClick={() => setBanner(null)} className="text-[#C9A96E] hover:text-[#FBF8F3] text-xl leading-none px-1">×</button>
            </div>
          </div>
        </div>
      )}

      {/* HERO */}
      <section className="relative z-10 grid lg:grid-cols-2 gap-10 items-center px-6 sm:px-10 pt-6 pb-20 max-w-7xl mx-auto">
        <div className="db-fade-up">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-5">The Curated First Date Marketplace</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#FBF8F3] tracking-tight">
            Value your time.<br /><span className="italic text-[#D4AF37]">Meet</span> authentically.
          </h1>
          <p className="mt-6 text-base sm:text-lg text-[#D8D2C9] max-w-lg leading-relaxed">
            DateBid turns intention into action. Discover remarkable people, place a respectful date offer, and skip the endless small talk — straight to a real evening worth remembering.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Link to="/auth?mode=register" data-testid="hero-cta-btn" className="db-offer-btn rounded-full px-7 py-3.5 text-sm font-semibold flex items-center gap-2">
              Make Your Move <ArrowRight size={16} />
            </Link>
            <button type="button" onClick={seeHow} data-testid="hero-seehow-btn"
              className="rounded-full px-7 py-3.5 text-sm font-semibold border border-[#D4AF37]/35 text-[#E6DCC6] hover:border-[#D4AF37]/70 hover:text-[#FBF8F3] transition-colors">
              See How It Works
            </button>
          </div>
          <div className="mt-6">
            <Link to="/auth" data-testid="hero-signin-link" className="text-sm text-[#D8D2C9] hover:text-[#FBF8F3] underline underline-offset-4 decoration-[#D4AF37]/40">I already have an account</Link>
          </div>
          <div className="mt-8 flex items-center gap-5 text-xs text-[#9E978E]">
            <span className="flex items-center gap-1.5"><BadgeCheck size={15} className="text-[#D4AF37]" /> 18+ only</span>
            <span className="flex items-center gap-1.5"><ShieldCheck size={15} className="text-[#D4AF37]" /> Identity verification available</span>
          </div>
        </div>

        <div className="relative db-fade-up flex items-center justify-center" style={{ animationDelay: "0.15s" }}>
          <div className="absolute inset-0 bg-[#751A32]/35 blur-[90px] rounded-full" />
          <div className="absolute inset-0 bg-[#D4AF37]/10 blur-[120px] rounded-full" />
          <BrandLogo
            variant="hero"
            data-testid="hero-brand-logo"
            className="relative w-full max-w-[300px] sm:max-w-[420px] lg:max-w-[560px] h-auto drop-shadow-[0_20px_55px_rgba(117,26,50,0.55)] db-logo-float"
          />
        </div>
      </section>

      {/* HOW A DATEBID OFFER WORKS */}
      <section id="how" className="relative z-10 px-6 sm:px-10 py-16 max-w-7xl mx-auto scroll-mt-24">
        <div className="db-gold-rule mb-10" />
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#D4AF37] mb-3">Simple by design</div>
          <h2 className="font-serif text-3xl sm:text-4xl text-[#FBF8F3] leading-tight">How a DateBid Offer Works</h2>
          <p className="mt-4 text-[#D8D2C9] leading-relaxed">Four clear steps — from discovering someone to meeting in person.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
          {STEPS.map((s, i) => (
            <div key={s.t} data-testid={`step-card-${i + 1}`} className="db-fade-up flex flex-col rounded-2xl border border-white/8 bg-[#141113]/60 overflow-hidden" style={{ animationDelay: `${i * 0.08}s` }}>
              <div className="relative h-56 sm:h-60 w-full overflow-hidden bg-black">
                <img src={s.img} alt={`${s.t} — DateBid`} loading="lazy" draggable={false}
                  className="absolute inset-0 w-full h-full object-cover object-top" />
                <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-[#141113] to-transparent" />
                <div className="absolute top-3 left-3 h-9 w-9 rounded-full bg-[#541224]/80 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37] backdrop-blur-sm"><s.icon size={17} /></div>
              </div>
              <div className="flex flex-col flex-1 p-6 pt-5">
                <div className="text-[11px] tracking-[0.18em] uppercase text-[#D4AF37] mb-1">Step {i + 1}</div>
                <h3 className="font-serif text-xl text-[#FBF8F3]">{s.t}</h3>
                <p className="text-sm text-[#9E978E] mt-2 leading-relaxed">{s.d}</p>
              </div>
            </div>
          ))}
        </div>

        {/* PAYMENT CLARIFICATION */}
        <div data-testid="payment-clarification" className="mt-10 rounded-2xl border border-[#D4AF37]/20 bg-[#141113]/70 p-6 sm:p-7 flex flex-col sm:flex-row gap-5 items-start">
          <div className="h-11 w-11 shrink-0 rounded-full bg-[#541224]/70 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37]"><Wallet size={20} /></div>
          <div>
            <h3 className="font-serif text-xl text-[#FBF8F3]">How payments work</h3>
            <p className="mt-2 text-sm text-[#D8D2C9] leading-relaxed">
              Every date offer is voluntary, and accepting one is always optional. Any amount two members agree on is arranged <span className="text-[#FBF8F3]">directly between them</span>. DateBid does not collect, hold, process, or guarantee money exchanged between members.
            </p>
            <p className="mt-2 text-xs text-[#9E978E] leading-relaxed">
              DateBid credits are a separate platform fee used to send an offer and unlock messaging — they are not the date amount.
            </p>
          </div>
        </div>
      </section>

      {/* MADE FOR HONOLULU */}
      <section className="relative z-10 px-6 sm:px-10 py-16 max-w-7xl mx-auto">
        <div data-testid="honolulu-section" className="rounded-3xl border border-[#D4AF37]/20 bg-gradient-to-br from-[#141113]/70 to-[#541224]/25 p-8 sm:p-12">
          <div className="flex items-center gap-2 text-[#D4AF37] text-[11px] uppercase tracking-[0.22em] mb-4"><MapPin size={15} /> Made for Honolulu</div>
          <h2 className="font-serif text-3xl sm:text-4xl text-[#FBF8F3] leading-tight max-w-3xl">Oahu singles, meet in person.</h2>
          <p className="mt-5 text-[#D8D2C9] leading-relaxed max-w-3xl">
            From Waikiki to Kakaako, Honolulu is full of people who'd rather meet than keep swiping. DateBid helps singles across Oahu turn interest into a real first date. Pick a public spot, like a sunset at Ala Moana Beach Park or a coffee in Kaimuki, and take it from there.
          </p>
          <Link to="/honolulu-first-date-ideas" data-testid="honolulu-ideas-link" className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#D4AF37] hover:text-[#E6C468] transition-colors">
            Safe first date ideas in Honolulu <ArrowRight size={15} />
          </Link>
          <div className="mt-3 flex flex-col gap-2">
            {HONOLULU_GUIDES.filter((g) => g.path !== "/honolulu-first-date-ideas").map((g, i) => (
              <Link key={g.path} to={g.path} data-testid={`honolulu-guide-link-${i + 1}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#D4AF37] hover:text-[#E6C468] transition-colors">
                {g.label} <ArrowRight size={15} />
              </Link>
            ))}
          </div>
          <p className="mt-5 text-xs text-[#9E978E] flex items-center gap-1.5"><BadgeCheck size={14} className="text-[#D4AF37]" /> 18+ only.</p>
        </div>
      </section>

      {/* TRUST & VERIFICATION */}
      <section className="relative z-10 px-6 sm:px-10 py-16 max-w-7xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#D4AF37] mb-3">Trust &amp; safety</div>
          <h2 data-testid="trust-heading" className="font-serif text-3xl sm:text-4xl text-[#FBF8F3] leading-tight">Verified. Respectful. Designed for Safer Dating.</h2>
          <p className="mt-4 text-[#D8D2C9] leading-relaxed">Real protections, honestly described — here is exactly what DateBid does to help you meet with confidence.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {TRUST.map((f, i) => (
            <div key={f.t} data-testid={`trust-card-${i + 1}`} className="db-fade-up rounded-2xl border border-white/8 bg-[#141113]/60 p-6" style={{ animationDelay: `${i * 0.08}s` }}>
              <div className="h-11 w-11 rounded-full bg-[#541224]/70 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37] mb-4"><f.icon size={19} /></div>
              <h3 className="font-serif text-lg text-[#FBF8F3]">{f.t}</h3>
              <p className="text-sm text-[#9E978E] mt-2 leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>

        {/* PRACTICAL SAFETY GUIDANCE */}
        <div data-testid="safety-guidance" className="mt-8 rounded-2xl border border-[#D4AF37]/20 bg-[#1a1113]/60 p-6 sm:p-7">
          <h3 className="font-serif text-xl text-[#FBF8F3] flex items-center gap-2"><ShieldCheck size={18} className="text-[#D4AF37]" /> Staying safe on your date</h3>
          <ul className="mt-3 grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm text-[#D8D2C9] leading-relaxed list-disc list-inside">
            <li>Always meet in a public place for first dates.</li>
            <li>Tell a friend your plans and when to expect you back.</li>
            <li>Never share your home address or financial details.</li>
            <li>DateBid will never ask you to send money through the app.</li>
          </ul>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative z-10 px-6 sm:px-10 py-16 max-w-3xl mx-auto">
        <div className="text-center mb-10">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#D4AF37] mb-3">FAQ</div>
          <h2 className="font-serif text-3xl sm:text-4xl text-[#FBF8F3]">Questions, answered</h2>
        </div>
        <div className="space-y-4" data-testid="faq-section">
          {FAQS.map((f, i) => (
            <details key={i} data-testid={`faq-item-${i + 1}`} className="group rounded-2xl border border-white/8 bg-[#141113]/60 p-5 open:border-[#D4AF37]/30 transition-colors">
              <summary className="flex items-center justify-between cursor-pointer list-none font-serif text-lg text-[#FBF8F3]">
                <span>{f.q}</span>
                <HelpCircle size={18} className="text-[#D4AF37] shrink-0 ml-3 transition-transform group-open:rotate-180" />
              </summary>
              <p className="mt-3 text-sm text-[#D8D2C9] leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="relative z-10 px-6 sm:px-10 pb-20 max-w-7xl mx-auto">
        <div className="rounded-3xl border border-[#D4AF37]/20 bg-gradient-to-br from-[#541224]/40 to-[#141113]/70 p-10 sm:p-14 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl text-[#FBF8F3] leading-tight">Ready to make a real evening happen?</h2>
          <p className="mt-4 text-[#D8D2C9] max-w-xl mx-auto leading-relaxed">Create your profile in a minute, then start discovering people worth your time.</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Link to="/auth?mode=register" data-testid="closing-cta-btn" className="db-offer-btn rounded-full px-8 py-3.5 text-sm font-semibold inline-flex items-center gap-2">
              Create your profile <ArrowRight size={16} />
            </Link>
            <Link to="/auth" data-testid="closing-signin-link" className="text-sm text-[#D8D2C9] hover:text-[#FBF8F3] underline underline-offset-4 decoration-[#D4AF37]/40">I already have an account</Link>
          </div>
        </div>
      </section>

      <footer className="relative z-10 px-6 sm:px-10 py-10 border-t border-white/8 text-center text-xs text-[#9E978E]">
        <p>DateBid · Meet in public places · Never share home addresses or financial details · Members must be 18+</p>
        {SAFETY_ENABLED && !LEGAL_ENABLED && <p className="mt-2"><Link to="/safety" data-testid="footer-safety-link" className="underline underline-offset-4 decoration-[#D4AF37]/40 hover:text-[#FBF8F3]">Safety Center</Link></p>}
        <BrandLogo variant="wordmarkSm" className="h-9 w-auto mx-auto mt-3 opacity-90" />
      </footer>
      {LEGAL_ENABLED && <SiteFooter />}
    </div>
  );
}
